import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import AdminTabs from "../components/AdminTabs";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, LogOut, RefreshCw, Search, Trash2, Mail, Phone,
  Building2, MessageSquare, Calendar, Loader2, Inbox,
} from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import WhatsAppIcon from "../components/WhatsAppIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-MX", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function AdminContacts() {
  const { user, token, logout, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts(data);
      if (data.length && !selected) setSelected(data[0]);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
      } else {
        setTimeout(() => toast.error("No se pudo cargar la lista"), 0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar este mensaje? No se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const remaining = contacts.filter((c) => c.id !== id);
      setContacts(remaining);
      if (selected?.id === id) setSelected(remaining[0] || null);
      setTimeout(() => toast.success("Mensaje eliminado"), 0);
    } catch {
      setTimeout(() => toast.error("Error al eliminar"), 0);
    }
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return contacts;
    const q = query.toLowerCase();
    return contacts.filter(
      (c) =>
        c.nombre?.toLowerCase().includes(q) ||
        c.empresa?.toLowerCase().includes(q) ||
        c.correo?.toLowerCase().includes(q) ||
        c.telefono?.toLowerCase().includes(q) ||
        c.mensaje?.toLowerCase().includes(q)
    );
  }, [contacts, query]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0A1B35] flex items-center justify-center">
        <Loader2 className="text-[#00B4D8] animate-spin" size={32} />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid="admin-contacts-page" className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#0A1B35] text-white border-b-2 border-[#00B4D8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 flex items-center justify-center border-2 border-[#00B4D8]" style={{ borderRadius: "2px" }}>
              <Shield size={16} className="text-[#00B4D8]" strokeWidth={2.5} />
            </span>
            <div>
              <div className="font-display font-black text-base tracking-tight leading-none">
                GUARDA<span className="text-slate-400"> FLEX</span>
                <sup className="ml-0.5 text-[9px] text-slate-400">™</sup>
              </div>
              <div className="font-mono-accent text-[9px] uppercase tracking-[0.25em] text-[#00B4D8] mt-1">
                Panel de mensajes
              </div>
            </div>
          </div>
          <AdminTabs />
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-xs text-white/60 uppercase tracking-wider">Sesión</div>
              <div className="text-sm font-semibold text-white" data-testid="admin-email">{user.email}</div>
            </div>
            <button
              onClick={fetchContacts}
              data-testid="admin-refresh"
              className="px-3 py-2 border border-white/15 hover:border-[#00B4D8] hover:text-[#00B4D8] text-xs uppercase tracking-wider transition-colors flex items-center gap-2"
              style={{ borderRadius: "2px" }}
            >
              <RefreshCw size={13} /> <span className="hidden sm:inline">Refrescar</span>
            </button>
            <button
              onClick={() => { logout(); navigate("/admin/login"); }}
              data-testid="admin-logout"
              className="px-3 py-2 bg-[#00B4D8] hover:bg-[#0096B4] text-xs uppercase tracking-wider transition-colors flex items-center gap-2 font-semibold"
              style={{ borderRadius: "2px" }}
            >
              <LogOut size={13} /> <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* List */}
          <div className="lg:w-2/5 flex flex-col">
            <div className="bg-white border border-slate-200 p-4 mb-4 flex items-center gap-3" style={{ borderRadius: "2px" }}>
              <Search size={16} className="text-slate-400 shrink-0" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="admin-search"
                placeholder="Buscar por nombre, empresa, correo, mensaje…"
                className="flex-1 text-sm outline-none placeholder:text-slate-400"
              />
              <span className="font-mono-accent text-[10px] uppercase tracking-wider text-slate-500">
                {filtered.length}/{contacts.length}
              </span>
            </div>

            <div className="bg-white border border-slate-200 overflow-hidden flex-1" style={{ borderRadius: "2px" }}>
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="text-[#00B4D8] animate-spin" size={28} />
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center px-6">
                  <Inbox size={36} className="text-slate-300 mb-4" />
                  <p className="font-display font-bold text-[#0A1B35]">Sin mensajes</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {query ? "Sin coincidencias en la búsqueda." : "Aún no hay solicitudes recibidas."}
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
                  {filtered.map((c) => {
                    const isSelected = selected?.id === c.id;
                    return (
                      <li key={c.id}>
                        <button
                          onClick={() => setSelected(c)}
                          data-testid={`admin-contact-item-${c.id}`}
                          className={`w-full text-left p-4 hover:bg-slate-50 transition-colors relative ${isSelected ? "bg-slate-50" : ""}`}
                        >
                          {isSelected && (
                            <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#00B4D8]" />
                          )}
                          <div className="flex items-start justify-between gap-3 mb-1">
                            <div className="font-semibold text-[#0A1B35] text-sm truncate">
                              {c.nombre}
                            </div>
                            <div className="font-mono-accent text-[10px] text-slate-400 shrink-0">
                              {formatDate(c.created_at)}
                            </div>
                          </div>
                          {c.empresa && (
                            <div className="text-xs text-slate-500 mb-1 truncate">{c.empresa}</div>
                          )}
                          <div className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                            {c.mensaje}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Detail */}
          <div className="lg:w-3/5">
            {selected ? (
              <div className="bg-white border border-slate-200 p-6 lg:p-8 sticky top-6" style={{ borderRadius: "2px" }} data-testid="admin-contact-detail">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div>
                    <div className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#00B4D8] mb-2">
                      Lead recibido
                    </div>
                    <h2 className="font-display font-black text-[#0A1B35] text-2xl tracking-tight">
                      {selected.nombre}
                    </h2>
                    {selected.empresa && (
                      <p className="text-sm text-slate-600 mt-1">{selected.empresa}</p>
                    )}
                  </div>
                  <button
                    onClick={() => onDelete(selected.id)}
                    data-testid="admin-delete-contact"
                    className="p-2 border border-slate-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600 text-slate-500 transition-colors"
                    style={{ borderRadius: "2px" }}
                    aria-label="Eliminar"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <a
                    href={`mailto:${selected.correo}`}
                    className="bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-[#00B4D8] p-4 transition-colors flex items-center gap-3"
                    style={{ borderRadius: "2px" }}
                  >
                    <Mail size={16} className="text-[#00B4D8] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Correo</div>
                      <div className="text-sm font-semibold text-[#0A1B35] truncate">{selected.correo}</div>
                    </div>
                  </a>
                  <a
                    href={`tel:${selected.telefono}`}
                    className="bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-[#00B4D8] p-4 transition-colors flex items-center gap-3"
                    style={{ borderRadius: "2px" }}
                  >
                    <Phone size={16} className="text-[#00B4D8] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Teléfono</div>
                      <div className="text-sm font-semibold text-[#0A1B35] truncate">{selected.telefono}</div>
                    </div>
                  </a>
                  {selected.empresa && (
                    <div className="bg-slate-50 border border-slate-200 p-4 flex items-center gap-3" style={{ borderRadius: "2px" }}>
                      <Building2 size={16} className="text-slate-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Empresa</div>
                        <div className="text-sm font-semibold text-[#0A1B35] truncate">{selected.empresa}</div>
                      </div>
                    </div>
                  )}
                  <div className="bg-slate-50 border border-slate-200 p-4 flex items-center gap-3" style={{ borderRadius: "2px" }}>
                    <Calendar size={16} className="text-slate-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Recibido</div>
                      <div className="text-sm font-semibold text-[#0A1B35]">{formatDate(selected.created_at)}</div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare size={14} className="text-[#00B4D8]" />
                    <div className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-slate-500">
                      Mensaje
                    </div>
                  </div>
                  <div className="bg-slate-50 border-l-4 border-[#00B4D8] p-5 text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">
                    {selected.mensaje}
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href={`mailto:${selected.correo}?subject=Re: tu solicitud a Guarda Flex&body=Hola ${encodeURIComponent(selected.nombre)},%0D%0A%0D%0A`}
                    className="gf-btn-primary"
                  >
                    <Mail size={14} /> Responder por correo
                  </a>
                  <a
                    href={`https://wa.me/52${selected.telefono.replace(/\D/g, "")}?text=${encodeURIComponent(`Hola ${selected.nombre}, soy de Guarda Flex. Recibimos tu solicitud.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gf-btn-secondary"
                  >
                    <WhatsAppIcon size={14} /> WhatsApp
                  </a>
                </div>

                <div className="mt-6 font-mono-accent text-[10px] text-slate-400">
                  ID: {selected.id}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 p-12 text-center" style={{ borderRadius: "2px" }}>
                <Inbox size={36} className="text-slate-300 mb-4 mx-auto" />
                <p className="text-slate-500">Selecciona un mensaje para ver el detalle</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
